import { useId, useRef, useState } from 'react'
import { useClickOutside } from '../../../hooks/useClickOutside'
import { cn } from '../../../utils/cn'
import { ALL } from '../../../data/bookingFilters'

export type AdvancedFilterDropdownProps = {
  value: string
  onChange: (value: string) => void
}

const FILTER_OPTIONS = [
  'Upcoming',
  'Completed Today',
  'Pending Payment',
  'Unassigned Driver',
  'Cancelled',
  'High Value (> €500)',
]

export const AdvancedFilterDropdown = ({ value, onChange }: AdvancedFilterDropdownProps) => {
  const [open, setOpen] = useState(false)
  const [tempValue, setTempValue] = useState<string>(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useClickOutside(containerRef, () => {
    setOpen(false)
    setTempValue(value)
  }, open)

  const handleApply = () => {
    onChange(tempValue)
    setOpen(false)
  }

  const handleCancel = () => {
    setTempValue(value)
    setOpen(false)
  }

  const selectedLabel = value !== ALL ? value : 'All Status'

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        aria-label="Filter"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => {
          setTempValue(value)
          setOpen((isOpen) => !isOpen)
        }}
        className="flex w-full h-[65px] items-center justify-between gap-[12px] rounded-[12px] border border-solid border-dash-field bg-white px-[18px]"
      >
        <span className="truncate text-left font-host text-[16px] font-medium leading-normal text-dash-field-text">
          {selectedLabel}
        </span>
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={cn("shrink-0 transition-transform duration-200", open && "-scale-y-100")}>
          <path d="M9 12L15 18L21 12" stroke="#555555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div
          id={listId}
          className="absolute inset-x-0 z-50 flex flex-col rounded-[12px] bg-white shadow-[0px_4px_24px_rgba(0,0,0,0.12)] overflow-hidden min-w-[260px]"
          style={{ top: 'calc(100% + 10px)' }}
        >

          {/* Options */}
          <ul role="listbox" className="flex flex-col py-2">
            {/* Provide 'All' option if we want to reset */}
            <li className="px-2">
              <button
                type="button"
                role="option"
                aria-selected={tempValue === ALL}
                onClick={() => setTempValue(ALL)}
                className={cn(
                  'w-full truncate text-left font-host text-[15px] px-4 py-3 rounded-[8px] transition-colors',
                  tempValue === ALL ? 'bg-[#F2F2F2] font-semibold text-black' : 'text-black hover:bg-gray-50',
                )}
              >
                All Status
              </button>
            </li>
            
            {FILTER_OPTIONS.map((option) => {
              const isSelected = tempValue === option
              return (
                <li key={option} className="px-2">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setTempValue(option)}
                    className={cn(
                      'w-full truncate text-left font-host text-[15px] px-4 py-3 rounded-[8px] transition-colors',
                      isSelected ? 'bg-[#F2F2F2] font-semibold text-black' : 'text-black hover:bg-gray-50',
                    )}
                  >
                    {option}
                  </button>
                </li>
              )
            })}
          </ul>

          {/* Footer Actions */}
          <div className="flex gap-3 px-4 py-4 border-t border-gray-100">
            <button
              onClick={handleCancel}
              className="flex-1 h-[40px] rounded-[8px] border border-[#E0E0E0] bg-white font-host text-[15px] font-medium text-black hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex-1 h-[40px] rounded-[8px] bg-[#1B3B2B] font-host text-[15px] font-medium text-white hover:opacity-90 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
